import React, { useState } from 'react'
import intl from 'react-intl-universal'
import { Button } from 'reactstrap'
import LinkListSubtypesConfigurationModal from './LinkListSubtypesConfigurationModal'
import Tabs from '~/reactapp/shared/Tabs'
import { EllipsisReduxStore, PicklistItem } from '~/types'
import SubtypeTable from './SubtypeTable'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const styles = require('./LinkListSubtypesConfiguration.styles.scss')

type SubtypeLists = {
  linkListSubtypes: Required<EllipsisReduxStore['settings']['linkListSubtypes']>
  linkSubtypes: Required<EllipsisReduxStore['settings']['linkSubtypes']>
}

type SubtypeConfigWithNewId = PicklistItem & { newId?: string }

type Props = Pick<
  EllipsisReduxStore['settings'],
  'linkListSubtypes' | 'linkSubtypes'
> & {
  onChange: (subtypeLists: SubtypeLists) => void
}

export default function LinkListSubtypesConfiguration(props: Props) {
  const { linkListSubtypes = [], linkSubtypes = [] } = props
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const listToUpdate = activeTabIndex === 0 ? linkListSubtypes : linkSubtypes

  const [modalState, setModalState] = useState({
    open: false,
    edit: false,
    entry: {
      id: '',
      text: '',
      active: true,
      default: false,
    },
  })

  const [defaultSubtypes, setDefaultSubtypes] = useState(() => {
    const existingListDefault = linkListSubtypes.find((st) => st.default)
    const existingLinkDefault = linkSubtypes.find((st) => st.default)

    return [
      existingListDefault?.id ?? 'None',
      existingLinkDefault?.id ?? 'None',
    ]
  })

  const [sortOrder, setSortOrder] = useState({
    sortKey: 'text',
    reversed: false,
  })

  // Given a subtype list, determines what settings list to update based on active tab and makes the update
  const handleOnChange = (updatedList) => {
    const updatedListSubtypes =
      activeTabIndex === 0 ? updatedList : linkListSubtypes
    const updatedLinkSubtypes =
      activeTabIndex === 1 ? updatedList : linkSubtypes

    props.onChange({
      linkListSubtypes: updatedListSubtypes,
      linkSubtypes: updatedLinkSubtypes,
    })
  }

  const handleModalData = (
    modalData: SubtypeConfigWithNewId,
    editAction: boolean
  ) => {
    const processEdit = () => {
      const updatedList = [...listToUpdate]
      const index = listToUpdate.findIndex((st) => st.id === modalData.id)
      // fall back to old ID if new id somehow not captured
      modalData.id = modalData.newId || modalData.id
      delete modalData.newId
      updatedList.splice(index, 1, { ...modalData })
      return updatedList
    }

    editAction
      ? handleOnChange(processEdit())
      : handleOnChange([modalData, ...listToUpdate])
  }

  const handleEdit = (subtype) => {
    setModalState({
      open: true,
      edit: true,
      entry: subtype,
    })
  }

  const handleDelete = (subtype) => {
    handleOnChange(listToUpdate.filter((st) => st.id !== subtype.id))
  }

  const toggleActive = (subtype) => {
    // Need to revert default to 'None' if default is made inactive
    const adjustDefault = (isActive, subtypeId) => {
      if (defaultSubtypes[activeTabIndex] === subtypeId && isActive) {
        setDefaultSubtypes(
          activeTabIndex === 0
            ? [subtypeId, defaultSubtypes[1]]
            : [defaultSubtypes[0], subtypeId]
        )
      }
      return false
    }
    const updatedList = [...listToUpdate]
    const index = listToUpdate.findIndex((st) => st.id === subtype.id)
    updatedList.splice(index, 1, {
      ...listToUpdate[index],
      active: !listToUpdate[index].active,
      default: adjustDefault(
        listToUpdate[index].active,
        listToUpdate[index].id
      ),
    })
    handleOnChange(updatedList)
  }

  const toggleDefault = (subtype) => {
    setDefaultSubtypes(
      activeTabIndex === 0
        ? [subtype.id, defaultSubtypes[1]]
        : [defaultSubtypes[0], subtype.id]
    )

    if (!subtype.default) {
      handleOnChange(
        listToUpdate.map((st) =>
          st.id !== subtype.id
            ? { ...st, default: false }
            : {
                ...st,
                default: true,
                active: true,
              }
        )
      )
    } else {
      handleOnChange(
        listToUpdate.map((st) => ({
          ...st,
          default: false,
        }))
      )
    }
  }

  const handleSort = (sortKey) => {
    const reversed =
      sortOrder.sortKey === sortKey ? !sortOrder.reversed : sortOrder.reversed
    setSortOrder({
      sortKey,
      reversed,
    })
    const originalList = [...listToUpdate]
    let sortedList = originalList.sort((stOne, stTwo) =>
      stOne[sortKey].localeCompare(stTwo[sortKey])
    )
    if (reversed) {
      sortedList = sortedList.reverse()
    }
    handleOnChange(sortedList)
  }

  return (
    <>
      <div className={`card mb-2 ${styles.root}`}>
        <h2 className="card-header">
          {intl.get('LinkListSubtypesConfiguration.title')}
        </h2>

        <div className="content">
          <Tabs
            activeTabIndex={activeTabIndex}
            onSelect={setActiveTabIndex}
            contentClass="tabs-content"
          >
            <Tabs.Tab
              title={intl.get(
                'LinkListSubtypesConfiguration.cardBody.listTabTitle'
              )}
            >
              <>
                <p>
                  {intl.get(
                    'LinkListSubtypesConfiguration.cardBody.listTabDescription'
                  )}
                </p>
                <Button
                  className="add-button"
                  onClick={() =>
                    setModalState({
                      open: true,
                      edit: false,
                      entry: {
                        id: '',
                        text: '',
                        active: true,
                        default: false,
                      },
                    })
                  }
                >
                  {intl.get(
                    'LinkListSubtypesConfiguration.cardBody.addSubtype'
                  )}
                </Button>
                {linkListSubtypes.length > 0 ? (
                  <SubtypeTable
                    data={linkListSubtypes}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleActive={toggleActive}
                    onToggleDefault={toggleDefault}
                  ></SubtypeTable>
                ) : (
                  <p>
                    {intl.get(
                      'LinkListSubtypesConfiguration.cardBody.noSubtypes'
                    )}
                  </p>
                )}
              </>
            </Tabs.Tab>

            <Tabs.Tab
              title={intl.get(
                'LinkListSubtypesConfiguration.cardBody.linkTabTitle'
              )}
            >
              <>
                <p>
                  {intl.get(
                    'LinkListSubtypesConfiguration.cardBody.linkTabDescription'
                  )}
                </p>
                <Button
                  className="add-button"
                  onClick={() =>
                    setModalState({
                      open: true,
                      edit: false,
                      entry: {
                        id: '',
                        text: '',
                        active: true,
                        default: false,
                      },
                    })
                  }
                >
                  {intl.get(
                    'LinkListSubtypesConfiguration.cardBody.addSubtype'
                  )}
                </Button>
                {linkSubtypes.length > 0 ? (
                  <SubtypeTable
                    data={linkSubtypes}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleActive={toggleActive}
                    onToggleDefault={toggleDefault}
                  ></SubtypeTable>
                ) : (
                  <p>
                    {intl.get(
                      'LinkListSubtypesConfiguration.cardBody.noLinkSubtypes'
                    )}
                  </p>
                )}
              </>
            </Tabs.Tab>
          </Tabs>
        </div>
      </div>

      {modalState.open && (
        <LinkListSubtypesConfigurationModal
          toggle={() =>
            setModalState({
              open: !modalState.open,
              edit: false,
              entry: {
                id: '',
                text: '',
                active: true,
                default: false,
              },
            })
          }
          modalState={modalState}
          existingIds={listToUpdate.map((st) => st.id)}
          onSubmit={handleModalData}
        />
      )}
    </>
  )
}
